import React from 'react';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ModalMessage from '../../UI/organisms/ModalMessage';
import LoadingMessage from '../../UI/organisms/AppLoadingMessage';
import moneyUtils from '../../../utils/moneyUtils';
import * as PaymentActions from '../../../actions/payment';
import { i18n, withTranslation } from 'next-i18next';
import appUtils from '../../../utils/appUtils';
import { X } from 'lucide-react';

interface RenewSubscriptionModalContentProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  mutating: boolean;
  currentPlan: any;
  hideModal: () => void;
  paymentActions: any;
  message: string;
  messageType: string;
  currentUser?: any;
  activePayment?: any;
}

function mapStateToProps(state: Record<string, any>) {
  const { payment, auth } = state;
  return {
    currentUser: auth.user,
    mutating: payment.mutatingSubscription,
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    paymentActions: bindActionCreators<any, any>(PaymentActions, dispatch),
  };
}

const RenewSubscriptionModalContent = (
  props: RenewSubscriptionModalContentProps,
) => {
  const {
    hideModal,
    t,
    currentUser,
    mutating,
    currentPlan,
    activePayment,
    paymentActions,
    message,
    messageType,
  } = props;

  const handleHideModal = (e) => {
    e.preventDefault();
    if (!mutating) hideModal();
  };

  const handleRenewal = () => {
    paymentActions.renew(activePayment.id);
  };

  const currentLocale = appUtils.getLocaleFromLang(i18n.language);
  const { ticket, sub_ticket } = currentUser.student_profile;
  const { currency_code, amount } = activePayment.price;
  let code = moneyUtils.currencyToCode(currency_code);
  if (currentLocale === 'pt') code = 'US$';
  const wrapperClass = classnames('max-w-sm mx-auto', { loading: mutating });
  const loadingMessage = mutating ? 'Processing...' : null;
  const loadingClass = 'modal-loading-message';
  const ticketCount = ticket + sub_ticket;
  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('student.billing.renewSubscription.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={handleHideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <LoadingMessage
            message={loadingMessage}
            loadingClass={loadingClass}
          />
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <div className="mb-4 checkout-review-explanation">
              {t('student.billing.renewSubscription.intro', {
                monthlyCharge: code + amount.toString(),
              })}
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8">
                <div className="grid grid-cols-12 gap-4">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.renewSubscription.planName')}
                      </strong>
                    </div>
                    <div className="float-right">{currentPlan.name}</div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-12 gap-4">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.renewSubscription.monthlyCharge')}
                      </strong>
                    </div>
                    <div className="float-right">
                      {code}
                      {amount}
                    </div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-12 gap-4">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.renewSubscription.chargeToday')}
                      </strong>
                    </div>
                    <div className="float-right">
                      {code}
                      {amount}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="control-group text-right">
            <Button
              variant="outline"
              disabled={mutating}
              onClick={handleHideModal}
            >
              {t('links.cancel')}
            </Button>
            <Button
              onClick={handleRenewal}
              type="submit"
              disabled={mutating || ticketCount != 0}
            >
              {t('links.renew')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(RenewSubscriptionModalContent);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
